import { ChatEntityKind } from 'src/api/FiretrolApiClient';

const REF_PREFIX = 'ref:';
// Ids are ints or GUIDs; anything else (slashes, dots, percent-escapes) could walk the path.
export const SAFE_VALUE = /^[A-Za-z0-9-]+$/;
const REF_LINK =
  /\[((?:\\.|\[[^\][\\]*\]|[^\][\\])*)\]\(<?(ref:[^)\s>]*)>?\)/gi;

export interface IEntityRef {
  kind: ChatEntityKind;
  params: Record<string, string>;
}

export interface IEntityRoute {
  keys: readonly string[];
  path: (params: Record<string, string>) => string;
}

export const entityRoutes: Record<ChatEntityKind, IEntityRoute> = {
  [ChatEntityKind.Job]: {
    keys: ['id'],
    path: (p) => `/jobs/${p.id}/summary`,
  },
  [ChatEntityKind.Rfi]: {
    keys: ['jobId', 'id'],
    path: (p) => `/jobs/${p.jobId}/rfis/${p.id}`,
  },
  [ChatEntityKind.Change]: {
    keys: ['jobId', 'id'],
    path: (p) => `/jobs/${p.jobId}/changes/view/${p.id}`,
  },
  [ChatEntityKind.Submittal]: {
    keys: ['jobId', 'id'],
    path: (p) => `/jobs/${p.jobId}/submittals/${p.id}`,
  },
  [ChatEntityKind.Drawing]: {
    keys: ['jobId', 'id'],
    path: (p) => `/jobs/${p.jobId}/drawings/${p.id}`,
  },
  [ChatEntityKind.Daily]: {
    keys: ['jobId', 'id'],
    path: (p) => `/jobs/${p.jobId}/dailys/view/${p.id}`,
  },
  [ChatEntityKind.Meeting]: {
    keys: ['jobId', 'id'],
    path: (p) => `/jobs/${p.jobId}/meetings/edit/${p.id}`,
  },
  [ChatEntityKind.Post]: {
    keys: ['jobId', 'id'],
    path: (p) => `/jobs/${p.jobId}/activity?postId=${p.id}`,
  },
  [ChatEntityKind.Timesheet]: {
    keys: ['jobId', 'userId', 'year', 'week'],
    path: (p) =>
      `/jobs/${p.jobId}/timesheets/user/${p.userId}/week/${p.year}/${p.week}`,
  },
};

const kindsByScheme = new Map<string, ChatEntityKind>(
  Object.keys(ChatEntityKind)
    .filter((name) => Number.isNaN(Number(name)))
    .map((name) => [
      name.toLowerCase(),
      ChatEntityKind[name as keyof typeof ChatEntityKind],
    ]),
);

export const isEntityRef = (href: string): boolean =>
  href.slice(0, REF_PREFIX.length).toLowerCase() === REF_PREFIX;

export const parseEntityRef = (href: string): IEntityRef | null => {
  if (!isEntityRef(href)) return null;
  const queryStart = href.indexOf('?');
  const scheme = href.slice(
    REF_PREFIX.length,
    queryStart < 0 ? undefined : queryStart,
  );
  const kind = kindsByScheme.get(scheme.toLowerCase());
  if (kind === undefined) return null;
  const params =
    queryStart < 0
      ? {}
      : Object.fromEntries(new URLSearchParams(href.slice(queryStart + 1)));
  return { kind, params };
};

/** @registry Maps a chat `ref:<kind>?<key>=<value>` link to its in-app route, or null when the kind is unknown or a key is missing or not an int/GUID. */
export const entityRefPath = (href: string): string | null => {
  const ref = parseEntityRef(href);
  if (!ref) return null;
  const route = entityRoutes[ref.kind];
  const valid = route.keys.every((key) =>
    SAFE_VALUE.test(ref.params[key] ?? ''),
  );
  return valid ? route.path(ref.params) : null;
};

/** @registry Absolute app URL (`origin` + in-app route) for a chat `ref:` link, or null when the href or origin is missing or the ref does not map to a route. */
export const entityRefUrl = (
  href: string | null | undefined,
  origin?: string,
): string | null => {
  if (!href || !origin) return null;
  const path = entityRefPath(href);
  return path === null ? null : `${origin}${path}`;
};

/** @registry Rewrites each chat `[label](ref:...)` entity link in text that leaves the chat bubble: with `origin`, to a Markdown link to the absolute app URL (Markdown export, print); without it, or for a ref with no route, to its label (clipboard copy). */
export const resolveEntityRefs = (text: string, origin?: string): string =>
  text.replace(REF_LINK, (_match, label: string, href: string) => {
    const url = entityRefUrl(href, origin);
    return url === null ? label : `[${label}](${url})`;
  });
